import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  X as CloseIcon,
  TrendingDown as RiskIcon,
  TrendingUp as OpportunityIcon,
  Cake as BirthdayIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export interface CustomerListItem {
  id: number;
  name: string;
  lastVisit: string;
  totalVisits: number;
  reason: string;
  riskLevel?: 'high' | 'medium' | 'low';
  phone?: string;
}

interface CustomerListModalProps {
  open: boolean;
  onClose: () => void;
  customers: CustomerListItem[];
  title: string;
  subtitle?: string;
  category: 'risk' | 'upsell' | 'birthday';
}

const CustomerListModal: React.FC<CustomerListModalProps> = ({
  open,
  onClose,
  customers,
  title,
  subtitle,
  category,
}) => {
  const navigate = useNavigate();

  const handleCustomerClick = (customerId: number) => {
    navigate(`/customers/${customerId}`);
    onClose();
  };

  const getCategoryIcon = () => {
    switch (category) {
      case 'risk':
        return <RiskIcon className="h-5 w-5 text-destructive" />;
      case 'upsell':
        return <OpportunityIcon className="h-5 w-5 text-green-600" />;
      case 'birthday':
        return <BirthdayIcon className="h-5 w-5 text-primary" />;
      default:
        return null;
    }
  };

  const getCategoryBadgeVariant = () => {
    switch (category) {
      case 'risk':
        return 'destructive' as const;
      case 'upsell':
        return 'default' as const;
      case 'birthday':
        return 'default' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getCategoryBadgeClassName = () => {
    switch (category) {
      case 'risk':
        return 'border-destructive text-destructive';
      case 'upsell':
        return 'border-green-600 text-green-700 bg-green-50';
      case 'birthday':
        return 'border-primary text-primary bg-blue-50';
      default:
        return '';
    }
  };

  const getRankColor = (totalVisits: number) => {
    if (totalVisits >= 30) return 'bg-gray-300'; // platinum
    if (totalVisits >= 20) return 'bg-yellow-400'; // gold
    if (totalVisits >= 10) return 'bg-gray-400'; // silver
    return 'bg-orange-400'; // bronze
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh]">
        <DialogHeader>
          <div className="flex items-center justify-between pr-6">
            <div className="flex items-center gap-2">
              {getCategoryIcon()}
              <div>
                <DialogTitle className="text-lg font-semibold">
                  {title}
                </DialogTitle>
                {subtitle && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute right-4 top-4"
            >
              <CloseIcon className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <Separator />

        <ScrollArea className="max-h-[calc(80vh-140px)]">
          <div className="space-y-0">
            {customers.map((customer, index) => (
              <React.Fragment key={customer.id}>
                <button
                  onClick={() => handleCustomerClick(customer.id)}
                  className="w-full flex items-start gap-3 p-4 hover:bg-accent transition-colors text-left"
                >
                  <Avatar className={`${getRankColor(customer.totalVisits)} w-12 h-12`}>
                    <AvatarFallback className="text-white font-bold text-lg">
                      {customer.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-sm">
                        {customer.name}
                      </p>
                      <Badge
                        variant="outline"
                        className="text-xs min-w-[50px] justify-center"
                      >
                        {customer.totalVisits}回
                      </Badge>
                    </div>

                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">
                        最終来店: {new Date(customer.lastVisit).toLocaleDateString('ja-JP')}
                      </p>
                      <Badge
                        variant={getCategoryBadgeVariant()}
                        className={`text-xs ${getCategoryBadgeClassName()}`}
                      >
                        {customer.reason}
                      </Badge>
                    </div>
                  </div>
                </button>
                {index < customers.length - 1 && <Separator />}
              </React.Fragment>
            ))}
          </div>

          {customers.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-sm text-muted-foreground">
                該当する顧客がいません
              </p>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerListModal;
